import { routes } from '@/config/routes'
import { useMixpanel } from '@/context/mixpanel'
import type { BorrowPositionsTotals } from '@/queries/borrowPositions.query'
import { cn } from '@/utils/cn'
import { formatUSD } from '@vaultsfyi/common/utils'
import { Link, generatePath } from 'react-router'
import type { Address } from 'viem'
import { Button } from '../common/button'
import type { AssetsBalanceGroup } from '../dashboard/useGroupedBalances/useGroupedBalances'
import type { LpTokensBalanceGroup } from '../dashboard/useGroupedBalances/useGroupedPositions'

interface Props {
  lpTokensBalance: LpTokensBalanceGroup | null
  assetsBalance: AssetsBalanceGroup | null
  borrowPositionsTotals: BorrowPositionsTotals | null | undefined
  account: Address | undefined
  className?: string
}

export function AccountBalances({ lpTokensBalance, assetsBalance, borrowPositionsTotals, account, className }: Props) {
  const { trackEvent } = useMixpanel()

  const idleAssets =
    assetsBalance === null ? null : assetsBalance?.reduce((acc, curr) => acc + curr.groupTotalBalance, 0)
  const lpTokens =
    lpTokensBalance === null ? null : lpTokensBalance?.reduce((acc, curr) => acc + curr.groupTotalBalance, 0)
  const borrowNetValue = borrowPositionsTotals === null ? null : borrowPositionsTotals && borrowPositionsTotals.netUsd

  const portfolioValue =
    lpTokens !== undefined && lpTokens !== null && idleAssets !== undefined && idleAssets !== null
      ? lpTokens + idleAssets + (borrowNetValue ?? 0)
      : undefined

  const portfolioValueDisplay = portfolioValue !== undefined ? formatUSD(portfolioValue) : undefined
  const idleAssetsDisplay = idleAssets !== undefined && idleAssets !== null ? formatUSD(idleAssets) : undefined

  return (
    <div className={cn('flex gap-4', className)}>
      <Button
        variant="default"
        size="default"
        className="flex flex-col items-start justify-start rounded-l-xl border-r px-3 text-left hocus:text-accent transition-colors"
        asChild
      >
        <Link
          to={generatePath(routes.dashboard, { address: account ?? '' })}
          onClick={() => trackEvent('Header portfolio value clicked')}
        >
          <span className="text-xs capitalize">Portfolio</span>
          <p className={cn('font-medium', portfolioValueDisplay && 'text-success')}>
            {portfolioValueDisplay ?? (
              <>
                <span className={cn(!portfolioValueDisplay && 'animate-pulse duration-700')}>–</span>
                <span className={cn(!portfolioValueDisplay && 'animate-pulse delay-300 duration-700')}>–</span>
              </>
            )}
          </p>
        </Link>
      </Button>
      <Button
        variant="default"
        size="default"
        className="flex flex-col items-start justify-start rounded-l-xl border-r px-3 text-left hocus:text-accent transition-colors"
        asChild
      >
        <Link
          to={generatePath(routes.dashboard, { address: account ?? '' })}
          onClick={() => trackEvent('Header idle assets clicked')}
        >
          <span className="text-xs capitalize">Idle assets</span>
          <p className={cn('font-medium', idleAssetsDisplay && 'text-success')}>
            {idleAssetsDisplay ?? (
              <>
                <span className={cn(!idleAssetsDisplay && 'animate-pulse duration-700')}>–</span>
                <span className={cn(!idleAssetsDisplay && 'animate-pulse delay-300 duration-700')}>–</span>
              </>
            )}
          </p>
        </Link>
      </Button>
    </div>
  )
}
